import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import type { World } from '../data/worlds';
import { getWorldLessons, type WorldLesson } from '../data/worldLessons';
import { useModalBehaviour } from '../hooks/useModalBehaviour';
interface WorldLessonsDialogProps {
  open: boolean;
  world: World | null;
  returnFocusTo: HTMLElement | null;
  onClose: () => void;
}
const EASE = [0.23, 1, 0.32, 1] as const;

/** One shared preview of a world's lessons. Bottom sheet on mobile, centered dialog from sm up. */
export function WorldLessonsDialog({
  open,
  world,
  returnFocusTo,
  onClose
}: WorldLessonsDialogProps) {
  const reduceMotion = useReducedMotion();
  const containerRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  useModalBehaviour({
    open,
    containerRef,
    dialogRef,
    initialFocusRef: headingRef,
    returnFocusTo,
    onClose
  });
  if (typeof document === 'undefined') return null;
  const lessons = world ? getWorldLessons(world.slug) : [];
  return createPortal(<div ref={containerRef} data-id="element-320" data-mp="3nkjreqc9lflt">
      <AnimatePresence data-id="element-321" data-mp="3njqh5fe3k7yx">
        {open && world && <div key="world-lessons" className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-6" data-id="element-322" data-mp="3ni703mkm9sw3">
            <motion.div aria-hidden="true" className="absolute inset-0 bg-ink/55" onClick={event => {
          if (event.target === event.currentTarget) onClose();
        }} initial={reduceMotion ? false : {
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={reduceMotion ? {
          opacity: 0,
          transition: {
            duration: 0
          }
        } : {
          opacity: 0
        }} transition={{
          duration: 0.2,
          ease: EASE
        }} data-id="element-323" data-mp="3dm41ursvyeef" />
            <motion.div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="world-lessons-title" aria-describedby="world-lessons-description" className="relative flex max-h-[88vh] w-full flex-col overflow-hidden rounded-t-[28px] border-2 border-b-0 border-ink bg-cream shadow-lift [max-height:88dvh] sm:max-w-[680px] sm:rounded-[28px] sm:border-b-2" initial={reduceMotion ? false : {
          opacity: 0,
          y: 32
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={reduceMotion ? {
          opacity: 0,
          transition: {
            duration: 0
          }
        } : {
          opacity: 0,
          y: 24
        }} transition={{
          duration: 0.24,
          ease: EASE
        }} data-id="element-324" data-mp="xvhip8m9vmh1">
              <header className="flex shrink-0 items-start gap-4 border-b border-ink/10 px-5 pb-4 pt-5 sm:px-7 sm:pt-6" data-id="element-325" data-mp="1qoura1fi3o0y">
                <div className="hidden h-[72px] w-[72px] shrink-0 rounded-2xl border-2 border-ink bg-cream-field p-1.5 sm:block" data-id="element-326" data-mp="1vmwbiponxte0">
                  <img src={world.art} alt="" width={world.width} height={world.height} decoding="async" className="h-full w-full object-contain" data-id="element-327" data-mp="k9al7172hlqw" />
                </div>
                <div className="min-w-0 flex-1" data-id="element-328" data-mp="lqhfoij7uw78">
                  <h2 id="world-lessons-title" ref={headingRef} tabIndex={-1} className="font-display text-[26px] leading-tight text-ink outline-none sm:text-[30px]" data-id="element-329" data-mp="lq7nqpem2eyw">
                    {world.name}
                  </h2>
                  <p id="world-lessons-description" className="mt-1 text-[16px] text-ink-soft sm:text-[17px]" data-id="element-330" data-mp="3l41ixzqwa077">
                    {world.description}
                  </p>
                  <p className="mt-2 text-[15px] font-bold text-ink" data-id="element-331" data-mp="3u4zsexk3qzx6">{lessons.length} adventures</p>
                </div>
                <button type="button" onClick={onClose} aria-label={`Close ${world.name} adventures`} className="-mr-2 -mt-1 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-ink transition-colors duration-150 ease-out hover:bg-cream-field focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink" data-id="element-332" data-mp="36evpytinh67r">
                  <XIcon className="h-6 w-6" aria-hidden="true" data-id="element-333" data-mp="3tujugejpvypu" />
                </button>
              </header>

              <div key={world.slug} tabIndex={0} aria-label={`${world.name} adventures`} className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ink sm:px-7" data-id="element-334" data-mp="1p69t2k8u373">
                <ul className="divide-y divide-ink/10" data-id="element-335" data-mp="2dfifko0qj2fl">
                  {lessons.map(lesson => <LessonRow key={lesson.id} lesson={lesson} data-id="element-336" data-mp="2idtl4mjzyqs0" />)}
                </ul>
              </div>

              <footer className="shrink-0 border-t border-ink/10 bg-cream px-5 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] sm:px-7 sm:pb-4" data-id="element-337" data-mp="zk8ttc4lwkfm">
                <p className="text-[15px] font-semibold text-ink-soft" data-id="element-338" data-mp="uqnv1hv6vmfd">
                  Explore these adventures in the Lightning Learn app.
                </p>
              </footer>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>, document.body);
}
function LessonRow({
  lesson
}: {
  lesson: WorldLesson;
}) {
  const [failed, setFailed] = useState(false);
  return <li className="flex items-center gap-4 py-3" data-id="element-339" data-mp="19hem2noyihhf">
      <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl border border-ink/15 bg-cream-field sm:h-[72px] sm:w-[72px]" data-id="element-340" data-mp="19g3hwmrln7ps">
        {!failed && <img src={lesson.image} alt={lesson.alt} width={1024} height={1024} loading="lazy" decoding="async" onError={() => setFailed(true)} className="h-full w-full object-contain" data-id="element-341" data-mp="3u7cfqy5cfiny" />}
      </div>
      <p className="min-w-0 flex-1 font-display text-[18px] leading-snug text-ink sm:text-[19px]" data-id="element-342" data-mp="3vpb1je4244or">{lesson.title}</p>
    </li>;
}